import { Component, EventEmitter, h, Listen, Prop, Event, State } from '@stencil/core';
import { FacetChangeEvent, getData, Option, FacetUpdateEvent, FacetItem } from '@hpfc/fc-frontend-core';

/**
 * @description The fccd-facet-multi-select creates a multi-select wrapped with a panel.
 * @use
 */
@Component({
  tag: 'fccd-facet-multi-select',
  styleUrl: 'facet-multi-select.scss',
})
export class FacetMultiSelect {
  @State() data: Array<Option>;

  multiSelectElement!: HTMLInputElement;

  /**
   * The id for the element in this component.
   */
  @Prop() elementId!: string;
  /**
   * The header text.
   */
  @Prop() panelLabel: string;
  /**
   * The Options to display
   */
  @Prop() options: any;
  /**
   * 'true' if the field is required, 'false' otherwise.
   */
  @Prop() required?: boolean = false;
  /**
   * 'true' we should hide the optional label, 'false' otherwise.
   */
  @Prop() hideInputOptionalLabel: boolean = true;
  /**
   * Dispatches this event when a selection is made.
   */
  @Event() fccdFacetChangeEvent: EventEmitter<FacetChangeEvent>;

  componentWillLoad() {
    if (!this.data) {
      this.data = getData(this.options);
    }
  }

  @Listen('hp5ValidationChange')
  componentChangeEvent(event: CustomEvent) {
    if (this.data) {
      this.data.forEach(d => {
        d.isSelected = event.detail.value.includes(this.encodeValue(d.value));
      });
    }

    const eventData = this.data.filter(o => o.isSelected).map(o => this.decodeValue(o.value));

    this.fccdFacetChangeEvent.emit(new FacetChangeEvent(event, new FacetItem(this.elementId, eventData, eventData.length > 0)));
  }

  @Listen('fccdComponentEvent', { target: 'document' })
  updateHandler(event: CustomEvent<FacetUpdateEvent>) {
    if (event?.detail?.facetOptionMap.has(this.elementId)) {
      //Encode these results.
      const optionMap = event.detail.facetOptionMap.get(this.elementId);

      this.data = optionMap.map(o => Object.assign({}, o));
    }
  }

  private getPanelName(): string {
    return `panel-${this.elementId}`;
  }

  /**
   * Encodes the value locally to deal with the comma issue, hp5-multi-select.
   * @param value
   */
  encodeValue(value: string): string {
    return value.replace(/,/g, '%2C');
  }

  /**
   * Decodes the value locally to handle returning the data.
   * @param value
   */
  decodeValue(value: string): string {
    return value.replace(/%2C/g, ',');
  }

  private getOptionsString(): string {
    if (this.data) {
      return JSON.stringify(
        this.data.map(d => {
          return { name: d.label, value: this.encodeValue(d.value) };
        }),
      );
    }

    return JSON.stringify([]);
  }

  private getSelectedString(): string {
    if (this.data) {
      return this.data
        .filter(d => d.isSelected)
        .map(d => this.encodeValue(d.value))
        .join(',');
    }

    return '';
  }

  render() {
    const hasData = typeof this.data == 'object';
    return (
      <hp5-filter-panel filter-panel-label={this.panelLabel} label-tag-selection="h3" element-id={this.getPanelName()}>
        {hasData && (
          <div>
            <hp5-multi-select
              ref={el => (this.multiSelectElement = el as HTMLInputElement)}
              element-id={this.elementId}
              label={this.panelLabel}
              name={this.elementId}
              options={this.getOptionsString()}
              options-selected={this.getSelectedString()}
              required={this.required}
              hide-input-optional-label={this.hideInputOptionalLabel}
            />
          </div>
        )}
      </hp5-filter-panel>
    );
  }
}
